import { platzhalterImWortlaut, type Baustein } from './typen';

/**
 * Der Textbaustein-Katalog der Baustellenordnung (Spezifikation E, Plan
 * Stufe B0): 83 Bausteine in den sieben Kapiteln des BG-BAU/INQA-Leitfadens.
 *
 * ═══ WIE DIESE DATEI ENTSTEHT ═════════════════════════════════════════════
 * Sie ist aus der Vorlage `docs/baustellenordnung/02-textbaustein-katalog.md`
 * erzeugt (02.11.2026), nicht von Hand geschrieben: Kennung, Titel, Wortlaut
 * und Warnung stehen Zeichen fuer Zeichen so wie dort, der Regelwerksbezug
 * ist nur in einzelne Vorschriften zerlegt. Wer etwas aendern will, aendert
 * die Vorlage und erzeugt die Datei neu.
 * `katalog.test.ts` liest die Vorlage und vergleicht — eine Handaenderung
 * hier liesse beide auseinanderlaufen und faellt dort auf.
 *
 * ═══ RECHTSSCHRANKE (Spezifikation E.2) ═══════════════════════════════════
 * Die DGUV-Nutzungsbestimmungen untersagen das Auslesen ihrer Inhalte durch
 * automatisierte Systeme; BG BAU verlangt fuer kommerzielle Verwendung eine
 * schriftliche Genehmigung. Daraus folgen drei Regeln, die ein spaeterer
 * Bearbeiter nicht aufheben darf, auch nicht, um einen Satz zu „verbessern":
 *  1. Jeder Baustein ist selbst formuliert. Kein Wortlaut aus DGUV-, BG-BAU-
 *     oder ASR-Veroeffentlichungen, auch nicht umgeschrieben oder eng
 *     angelehnt.
 *  2. Vorschriften werden nur BENANNT, nie zitiert. Nummer und Titel sind
 *     Fakt und nicht geschuetzt, der Regelwerkstext ist es: ein Baustein
 *     nennt „§ 9 DGUV Vorschrift 38", nie den Satz, der dort steht.
 *  3. Kein Einlesen von DGUV- oder BG-BAU-Texten als Quelle — weder in diesen
 *     Katalog noch in den KI-Kontext.
 *
 * ═══ VERSIONIERUNG (Spezifikation E.4) ════════════════════════════════════
 * Eine uebernommene Regel traegt ihren Wortlaut und `KATALOG_VERSION` in der
 * Datenbank; ein freigegebenes Dokument muss auch Jahre spaeter erklaeren
 * koennen, mit welchem Katalog es formuliert wurde. Deshalb wird ein Baustein
 * nie stillschweigend umformuliert: ein geaenderter Wortlaut bekommt eine
 * neue Kennung mit Suffix `.v2`, der alte Baustein bleibt stehen, und
 * `KATALOG_VERSION` steigt bei jeder inhaltlichen Aenderung.
 *
 * ═══ BEDINGUNGEN ══════════════════════════════════════════════════════════
 * `wenn` steht nur dort, wo die Bedingung der Vorlage ein Ja/Nein-Merkmal
 * der Planungsphase hat (Kran, Geruest, Erdarbeiten …). „Nur wenn ein
 * Bauaufzug vorhanden ist" hat keines: solche Bausteine tragen allein
 * `bedingungText` und werden angeboten, nie von selbst geschrieben (typen.ts).
 * Bedingungen, die in der Vorlage mit „immer" beginnen („immer, sobald
 * Fahrzeugverkehr …"), gelten hier als unbedingt; ihr Zusatz steht nur in
 * der Vorlage.
 */

export const KATALOG_VERSION = '2026-11-1';

/** Kennungspraefix → Kapitel aus `kapitel.ts` (Plan, Stufe B0). */
export const BAUSTEIN_KAPITEL_ABBILDUNG: Readonly<Record<string, string>> = {
    'bo.allgemein': 'allgemeine_informationen',
    'bo.notfall': 'notfallmanagement',
    'bo.regeln': 'allgemeine_regelungen',
    'bo.wege': 'verkehrswege',
    'bo.anschluesse': 'anschluesse',
    'bo.arbeitsmittel': 'arbeitsmittel',
    'bo.absturz': 'absturzsicherung',
};

/** Die Platzhalterliste wird aus dem Wortlaut abgeleitet, nie getrennt gepflegt. */
type BausteinOhnePlatzhalter = Omit<Baustein, 'platzhalter'>;

const BAUSTEINE: BausteinOhnePlatzhalter[] = [
    // ═══ Kapitel 1 — Allgemeine Informationen ══════════════════════════════
    {
        id: 'bo.allgemein.erlass_unterschrift',
        kapitel: 'allgemeine_informationen',
        titel: 'Erlass, Unterschrift und Fortschreibung',
        wortlaut: 'Diese Baustellenordnung wird von {erlassender} als Inhaber des Hausrechts erlassen und in der Fassung vom {stand_datum}, Revision {revision_nr}, von {unterzeichner} unterzeichnet. Sie wird bei erheblichen Änderungen des Bauablaufs fortgeschrieben; die neue Fassung wird an alle Auftragnehmer versandt und ersetzt die vorherige. Die geltende Fassung liegt während der Betriebszeit im Baubüro {aushangort} zur Einsicht aus.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 3 Abs. 3 Nr. 3 BaustellV',
            'RAB 31 (Sicherheits- und Gesundheitsschutzplan), Ziff. 3.1.3 und 3.1.4',
        ],
        hinweis: 'Das Unterschriftsfeld gehört dem Erlassenden. Das erstellende Ingenieurbüro wird ausschließlich in der Fußzeile als Verfasser genannt und unterschreibt nicht; der Koordinator erlässt das Dokument nicht und zeichnet es nicht ab. Eine nicht fortgeschriebene oder vor Ort nicht einsehbare Ordnung ist in der Prüfpraxis ein eigenständiger Mangel.',
    },
    {
        id: 'bo.allgemein.geltungsbereich_ort',
        kapitel: 'allgemeine_informationen',
        titel: 'Örtlicher Geltungsbereich der Baustellenordnung',
        wortlaut: 'Diese Baustellenordnung gilt auf dem gesamten Baustellengelände {baustelle_bezeichnung} in {baustelle_anschrift}, einschließlich der zugehörigen Lager-, Stell- und Sozialflächen sowie der eingerichteten Zufahrten. Der räumliche Umfang ist im Baustelleneinrichtungsplan {bep_stand} rot umrandet dargestellt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [],
        hinweis: 'Ohne eindeutige Grenze lässt sich weder das Hausrecht noch die Tragepflicht für Schutzausrüstung durchsetzen; angrenzende, aber nicht übergebene Flächen dürfen nicht miterfasst werden.',
    },
    {
        id: 'bo.allgemein.geltungsbereich_zeit',
        kapitel: 'allgemeine_informationen',
        titel: 'Zeitlicher Geltungsbereich und Übergabe',
        wortlaut: 'Die Baustellenordnung gilt ab der Übergabe der Baustelle an {erlassender} am {geltung_beginn} bis zur Räumung und Rückgabe des Geländes am {geltung_ende}. Für Flächen, die vorher oder nachher genutzt werden, gelten die Regelungen erst ab dem Zeitpunkt der jeweiligen Teilübergabe.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [],
        hinweis: 'Vor der Übergabe besteht kein Hausrecht des Erlassenden; eine rückwirkende Geltung ist nicht durchsetzbar.',
    },
    {
        id: 'bo.allgemein.adressatenkreis',
        kapitel: 'allgemeine_informationen',
        titel: 'Für wen die Ordnung gilt',
        wortlaut: 'Diese Baustellenordnung gilt für alle Personen, die die Baustelle betreten: Beschäftigte aller beauftragten Unternehmen und deren Nachunternehmer, Selbstständige ohne Beschäftigte, Lieferanten, Fahrer, Planer, Prüfer, Besucher und Vertreter des Bauherrn. Wer die Baustelle betritt, erkennt diese Regelungen an.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§§ 903, 1004 BGB (Hausrecht des unmittelbaren Besitzers)',
        ],
    },
    {
        id: 'bo.allgemein.verbindlichkeit',
        kapitel: 'allgemeine_informationen',
        titel: 'Verbindlichkeit über den Bauvertrag',
        wortlaut: 'Diese Baustellenordnung ist Bestandteil des Bauvertrages zwischen {erlassender} und dem jeweiligen Auftragnehmer und in der Fassung vom {stand_datum} verbindlich. Sie wird mit der Leistungsbeschreibung übergeben; die Auftragnehmer rechnen den daraus entstehenden Aufwand in ihre Angebote ein.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 5 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            '§ 4 Abs. 1 Nr. 1 VOB/B',
        ],
        hinweis: 'Eine Baustellenordnung, die erst nach Vertragsschluss übergeben wird, ist keine Vertragsgrundlage und begründet weder Mitwirkungs- noch Kostenfolgen.',
    },
    {
        id: 'bo.allgemein.weitergabe_nu',
        kapitel: 'allgemeine_informationen',
        titel: 'Weitergabepflicht an Nachunternehmer',
        wortlaut: 'Jeder Auftragnehmer gibt diese Baustellenordnung vollständig und in der jeweils geltenden Fassung an seine Nachunternehmer weiter und verpflichtet sie in gleicher Weise. Die Weitergabe wird der Bauleitung auf Verlangen nachgewiesen; die Verpflichtung setzt sich über alle weiteren Vergabestufen fort.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 5 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Ohne durchgereichte Verpflichtung besteht gegenüber dem Nachunternehmer kein Schuldverhältnis; vertragliche Folgen laufen dann ins Leere.',
    },
    {
        id: 'bo.allgemein.kenntnisnahme_unterschrift',
        kapitel: 'allgemeine_informationen',
        titel: 'Kenntnisnahme mit Unterschrift',
        wortlaut: 'Vor der ersten Tätigkeit auf der Baustelle bestätigt jedes Unternehmen die Kenntnisnahme dieser Baustellenordnung schriftlich mit Datum und Unterschrift einer vertretungsberechtigten Person. Beschäftigte, die die deutsche Sprache nicht ausreichend beherrschen, erhalten die Inhalte in einer für sie verständlichen Fassung. Die Bestätigungen werden bei der Bauleitung in {ablage_kenntnisnahme} geführt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 5 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Die Kenntnisnahme ersetzt die Unterweisung nach § 12 ArbSchG nicht; sie darf im Formular auch nicht so bezeichnet werden.',
    },
    {
        id: 'bo.allgemein.verhaeltnis_sigeplan',
        kapitel: 'allgemeine_informationen',
        titel: 'Verhältnis zum Sicherheits- und Gesundheitsschutzplan',
        wortlaut: 'Diese Baustellenordnung ergänzt den Sicherheits- und Gesundheitsschutzplan des Projekts, ersetzt ihn nicht. Der Plan regelt die gewerkübergreifende Koordinierung; diese Ordnung regelt das Verhalten auf dem Gelände und die gemeinsam genutzten Einrichtungen. Bei Abweichungen zwischen beiden Dokumenten gilt die strengere Regelung, und der Auftraggeber stellt die Widerspruchsfreiheit bis zur nächsten Fortschreibung her.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 2 Abs. 3 BaustellV',
            'RAB 31 (Sicherheits- und Gesundheitsschutzplan), Ziff. 3.3',
        ],
        hinweis: 'Jeder Punkt wird einmal entschieden und im jeweils anderen Dokument nur referenziert; doppelte, abweichende Regelungen sind in der Prüfpraxis ein Befund.',
    },
    {
        id: 'bo.allgemein.arbeitgeberpflichten',
        kapitel: 'allgemeine_informationen',
        titel: 'Arbeitgeberpflichten bleiben unberührt',
        wortlaut: 'Diese Baustellenordnung lässt die Pflichten jedes Arbeitgebers gegenüber seinen eigenen Beschäftigten unberührt. Gefährdungsbeurteilung, Unterweisung, Bereitstellung geeigneter Arbeitsmittel und persönlicher Schutzausrüstung sowie die Aufsicht über die eigenen Arbeiten verbleiben vollständig beim jeweiligen Unternehmen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§§ 5, 12 ArbSchG',
            '§ 5 BaustellV',
            'RAB 31, Ziff. 3.2 Nr. 4',
        ],
        hinweis: 'Fehlt dieser Satz, entsteht der Anschein einer Entlastung der Unternehmer — genau das, was § 5 BaustellV ausschließt.',
    },
    {
        id: 'bo.allgemein.projektorganisation',
        kapitel: 'allgemeine_informationen',
        titel: 'Ansprechpartner und ihre Befugnisse',
        wortlaut: 'Für das Projekt sind benannt: Bauherr {bauherr}, Bauleitung {bauleitung} mit den Rechten aus dem Bauvertrag, Sicherheits- und Gesundheitsschutzkoordinator {sigeko} mit koordinierender und überwachender Aufgabe. Der Koordinator erteilt keine Weisungen; er stimmt ab, überwacht und bereitet Anweisungen des Auftraggebers vor. Die vollständige Liste mit Erreichbarkeiten hängt im Baubüro aus und ist in Anlage {anlage_organigramm} enthalten.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 3 BaustellV',
            'RAB 30 (Geeigneter Koordinator)',
        ],
        hinweis: 'Dem Koordinator darf keine Anordnungs-, Weisungs- oder Hausrechtsbefugnis zugeschrieben werden; er ist weder Bauleiter nach Landesbauordnung noch Fachkraft für Arbeitssicherheit noch Koordinator nach DGUV Regel 101-004.',
    },
    {
        id: 'bo.allgemein.weisungsbefugte_person',
        kapitel: 'allgemeine_informationen',
        titel: 'Person für die Abstimmung der Arbeiten',
        wortlaut: 'Für die Abstimmung der Arbeiten mehrerer Unternehmen bestimmt der Auftraggeber {abstimmperson}. Zur Abwehr besonderer Gefahren, die aus dem Zusammenwirken der Gewerke entstehen, ist diese Person weisungsbefugt; die Befugnis beschränkt sich auf Ort, Zeit und Reihenfolge der Arbeiten, Sperrbereiche und gemeinsam genutzte Einrichtungen. Anweisungen werden an die Aufsichtführenden der betroffenen Unternehmen gerichtet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 6 Abs. 1 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            '§ 8 ArbSchG',
        ],
        hinweis: 'Personen-, ablauf- oder verfahrensbezogene Anweisungen an fremde Beschäftigte begründen den Verdacht der Arbeitnehmerüberlassung; die Befugnis ist deshalb schriftlich begrenzt zu halten. Das sofortige Stoppen bei Gefahr im Verzug bleibt davon unberührt.',
    },
    {
        id: 'bo.allgemein.uebergreifende_gefaehrdungen',
        kapitel: 'allgemeine_informationen',
        titel: 'Information über gewerkübergreifende Gefährdungen',
        wortlaut: 'Auf dieser Baustelle bestehen Gefährdungen, die über das einzelne Gewerk hinauswirken: {uebergreifende_gefaehrdungen}. Die betroffenen Unternehmen berücksichtigen sie in ihrer eigenen Gefährdungsbeurteilung und stimmen ihre Maßnahmen vor Arbeitsbeginn mit der Bauleitung ab.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 8 ArbSchG',
            'RAB 31, Ziff. 3.2',
        ],
        hinweis: 'Der Platzhalter darf nicht aus einer Standardliste gefüllt werden; welche Gefährdungen tatsächlich bestehen, ergibt sich aus dem örtlichen und zeitlichen Zusammentreffen der konkreten Arbeiten.',
    },

    // ═══ Kapitel 2 — Notfallmanagement ═════════════════════════════════════
    {
        id: 'bo.notfall.notruf',
        kapitel: 'notfallmanagement',
        titel: 'Notruf absetzen',
        wortlaut: 'Bei Unfall, Brand oder anderen Notfällen wird zuerst der Notruf 112 abgesetzt und anschließend die Bauleitung unter {telefon_bauleitung} verständigt. Die für die Rettungsleitstelle nötigen Angaben — Baustellenanschrift {baustelle_anschrift}, Anfahrt über {rettungszufahrt}, Art der Verletzung, Zahl der Betroffenen — hängen an jedem Erste-Hilfe-Punkt und in den Sozialcontainern aus.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 10 ArbSchG',
            '§ 24 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
    },
    {
        id: 'bo.notfall.meldekette',
        kapitel: 'notfallmanagement',
        titel: 'Interne Meldekette',
        wortlaut: 'Jeder Unfall, jeder Beinaheunfall und jedes Ereignis mit Sachschaden wird der Bauleitung noch am selben Tag gemeldet, auch wenn keine Verletzung eingetreten ist. Die Meldung erfolgt an {meldestelle}; die Unfallstelle bleibt bis zur Freigabe durch die Bauleitung unverändert, soweit dadurch niemand gefährdet wird.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 193 SGB VII (Pflicht zur Anzeige eines Versicherungsfalls durch die Unternehmer)',
        ],
        hinweis: 'Die Unfallanzeige an den Unfallversicherungsträger bleibt Sache des Arbeitgebers des Verunfallten; die Meldung an die Bauleitung tritt daneben, nicht an ihre Stelle.',
    },
    {
        id: 'bo.notfall.anwesenheit',
        kapitel: 'notfallmanagement',
        titel: 'An- und Abmeldung auf der Baustelle',
        wortlaut: 'Jede Person meldet sich beim Betreten der Baustelle an {anmeldestelle} an und beim Verlassen wieder ab. Die Anwesenheitsübersicht dient ausschließlich der Vollzähligkeitskontrolle im Räumungsfall und der Zutrittssteuerung; eine Auswertung zu Leistungs- oder Verhaltenskontrolle findet nicht statt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'Art. 5 Abs. 1 lit. b und Art. 6 Abs. 1 lit. f DSGVO',
        ],
        hinweis: 'Der genannte Zweck bindet: eine spätere Nutzung der Zeitstempel für Leistungskontrolle ist eine unzulässige Umwidmung und zusätzlich mitbestimmungspflichtig beim Betriebsrat des jeweiligen Arbeitgebers.',
    },
    {
        id: 'bo.notfall.lotsenpunkt',
        kapitel: 'notfallmanagement',
        titel: 'Lotsenpunkt für Rettungskräfte',
        wortlaut: 'Der Lotsenpunkt für Rettungskräfte liegt an {lotsenpunkt}. Wer den Notruf abgesetzt hat, sorgt dafür, dass dort eine eingewiesene Person die Rettungskräfte in Empfang nimmt und ohne Umweg zur Einsatzstelle führt. Der Lotsenpunkt ist gekennzeichnet und aus Richtung {anfahrtsrichtung} anfahrbar.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.3 (Sicherheits- und Gesundheitsschutzkennzeichnung)',
        ],
    },
    {
        id: 'bo.notfall.rettungszufahrt',
        kapitel: 'notfallmanagement',
        titel: 'Rettungszufahrt freihalten',
        wortlaut: 'Die Rettungszufahrt {rettungszufahrt} wird in voller Breite und Höhe freigehalten. Abstellen von Fahrzeugen, Lagern von Material und Aufstellen von Geräten sind dort untersagt; kurzzeitige Nutzungen für Anlieferungen werden vorher mit der Bauleitung abgestimmt und durch eine ansprechbare Person begleitet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A2.3 (Fluchtwege und Notausgänge, Flucht- und Rettungsplan)',
        ],
    },
    {
        id: 'bo.notfall.sammelstelle',
        kapitel: 'notfallmanagement',
        titel: 'Sammelstelle nach Räumung',
        wortlaut: 'Nach einem Räumungssignal begeben sich alle Personen unverzüglich zur Sammelstelle {sammelplatz} und bleiben dort, bis die Vollzähligkeit festgestellt ist. Jeder Aufsichtführende meldet die Vollzähligkeit seiner Beschäftigten an die Bauleitung. Die Baustelle wird erst nach ausdrücklicher Freigabe wieder betreten.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 10 ArbSchG',
            'ASR A2.3 (Fluchtwege und Notausgänge, Flucht- und Rettungsplan)',
        ],
    },
    {
        id: 'bo.notfall.alarmierung',
        kapitel: 'notfallmanagement',
        titel: 'Alarmierung und Räumungssignal',
        wortlaut: 'Die Räumung wird durch {alarmsignal} ausgelöst. Das Signal wird vor Aufnahme des Betriebs allen Beteiligten bekannt gemacht und in Abständen von {probealarm_intervall} erprobt. Wer eine Gefahr erkennt, die eine Räumung erfordert, löst das Signal aus oder veranlasst die Auslösung über {meldestelle}.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [
            'ASR A2.3 (Fluchtwege und Notausgänge, Flucht- und Rettungsplan)',
        ],
    },
    {
        id: 'bo.notfall.ersthelfer',
        kapitel: 'notfallmanagement',
        titel: 'Ersthelfer der Unternehmen',
        wortlaut: 'Jedes Unternehmen stellt für seine Beschäftigten eigene ausgebildete Ersthelfer; bei mehr als 20 anwesenden Versicherten sind das 10 Prozent der eigenen Anwesenden. Die Namen der anwesenden Ersthelfer werden der Bauleitung zu Beginn des Einsatzes gemeldet und am Aushang {aushang_ersthelfer} geführt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 26 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Die Quote gilt je Arbeitgeber für dessen eigene anwesende Beschäftigte, nicht als Prozentsatz der gesamten Baustellenbelegschaft; eine gemeinsame Quote entlastet kein Unternehmen.',
    },
    {
        id: 'bo.notfall.erste_hilfe_material',
        kapitel: 'notfallmanagement',
        titel: 'Erste-Hilfe-Material und Erste-Hilfe-Raum',
        wortlaut: 'Erste-Hilfe-Material steht an {erste_hilfe_punkte} bereit und ist gekennzeichnet. Ein Erste-Hilfe-Raum ist in {erste_hilfe_raum} eingerichtet. Jedes Unternehmen hält für seine Beschäftigten eigenes Erste-Hilfe-Material an den Arbeitsplätzen vor; entnommenes Material wird unverzüglich ersetzt und der Bauleitung gemeldet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A4.3 (Erste-Hilfe-Räume, Mittel und Einrichtungen zur Ersten Hilfe)',
            '§ 24 DGUV Vorschrift 1',
        ],
    },
    {
        id: 'bo.notfall.durchgangsarzt',
        kapitel: 'notfallmanagement',
        titel: 'Ärztliche Versorgung nach Unfall',
        wortlaut: 'Nach einem Arbeitsunfall mit Behandlungsbedarf wird ein Durchgangsarzt aufgesucht; zuständig ist {durchgangsarzt} in {durchgangsarzt_anschrift}. Das nächstgelegene Krankenhaus mit Notaufnahme ist {krankenhaus}. Jede Erste-Hilfe-Leistung wird vom jeweiligen Arbeitgeber dokumentiert und aufbewahrt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 34 SGB VII (Durchführung der Heilbehandlung)',
            '§ 24 DGUV Vorschrift 1',
        ],
    },
    {
        id: 'bo.notfall.brandschutz_ausruestung',
        kapitel: 'notfallmanagement',
        titel: 'Feuerlöscheinrichtungen',
        wortlaut: 'Feuerlöscher stehen an {loeschmittel_standorte} bereit und werden freigehalten und gekennzeichnet. Jedes Unternehmen bringt für seine brandgefährlichen Arbeiten zusätzlich eigene, für den Brandfall geeignete Löschmittel an den Arbeitsplatz mit. Ein benutzter Löscher wird nicht zurückgestellt, sondern der Bauleitung übergeben.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A2.2 (Maßnahmen gegen Brände)',
        ],
    },
    {
        id: 'bo.notfall.heissarbeiten_freigabe',
        kapitel: 'notfallmanagement',
        titel: 'Freigabe für Arbeiten mit Brandgefahr',
        wortlaut: 'Schweißen, Schneiden, Trennschleifen, Löten, Auftauen und alle weiteren Arbeiten mit offener Flamme oder Funkenflug werden erst nach schriftlicher Freigabe durch {freigabestelle} begonnen. Die Freigabe legt Umfang, Zeitfenster, Schutzmaßnahmen im Gefahrbereich und die Dauer der Brandwache nach Arbeitsende fest und wird an der Arbeitsstelle mitgeführt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A2.2 (Maßnahmen gegen Brände)',
            'TRGS 800 (Brandschutzmaßnahmen)',
        ],
        wenn: ['bestand'],
        bedingungText: 'nur wenn Arbeiten mit Brand- oder Explosionsgefahr vorkommen; im Bestand und in explosionsgefährdeten Bereichen zwingend',
        hinweis: 'Die Freigabe ersetzt die Gefährdungsbeurteilung des ausführenden Unternehmens nicht; die Festlegung der Schutzmaßnahmen im Freigabeschein trifft eine fachkundige Person.',
    },

    // ═══ Kapitel 3 — Allgemeine Regelungen ═════════════════════════════════
    {
        id: 'bo.regeln.betriebszeit',
        kapitel: 'allgemeine_regelungen',
        titel: 'Betriebszeit der Baustelle',
        wortlaut: 'Die Baustelle ist von {arbeitszeit_beginn} bis {arbeitszeit_ende} an {arbeitstage} in Betrieb. Arbeiten außerhalb dieser Zeiten, an Sonn- und Feiertagen sowie Nachtarbeit werden mindestens {vorlauf_sonderzeit} vorher bei der Bauleitung beantragt und erst nach schriftlicher Zustimmung aufgenommen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 ArbZG (Sonn- und Feiertagsruhe)',
            'AVV Baulärm (Allgemeine Verwaltungsvorschrift zum Schutz gegen Baulärm)',
        ],
        hinweis: 'Die Betriebszeit der Baustelle ist keine Arbeitszeitregelung für fremde Beschäftigte; Arbeitszeit, Pausen und Ruhezeiten bestimmt jeder Arbeitgeber selbst.',
    },
    {
        id: 'bo.regeln.zutritt_besucher',
        kapitel: 'allgemeine_regelungen',
        titel: 'Zutritt und Begleitung von Besuchern',
        wortlaut: 'Besucher, Lieferanten und Fahrer melden sich an {anmeldestelle} an, erhalten eine kurze Sicherheitsinformation und betreten den Baubereich nur in Begleitung einer ortskundigen Person des empfangenden Unternehmens. Besucher tragen die für den betretenen Bereich vorgeschriebene persönliche Schutzausrüstung; das empfangende Unternehmen stellt sie bereit.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 903 BGB (Hausrecht)',
            '§ 9 ArbSchG (besondere Gefahren)',
        ],
    },
    {
        id: 'bo.regeln.nachunternehmer',
        kapitel: 'allgemeine_regelungen',
        titel: 'Anzeige der Weitervergabe',
        wortlaut: 'Jede Weitervergabe von Leistungen wird der Bauleitung vor Arbeitsbeginn schriftlich angezeigt, mit Firma, Leistungsumfang, verantwortlicher Kontaktperson und Aufsichtführendem vor Ort. Nicht angezeigte Unternehmen erhalten keinen Zutritt zur Baustelle.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 5 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            '§ 5 Abs. 3 DGUV Vorschrift 1 (Aufsichtführender)',
        ],
        hinweis: 'Für die Zutrittsprüfung dürfen nur die Daten erhoben werden, die die Baustellensicherheit oder ein gesetzlicher Nachweis erfordern; die Mitführungspflicht für Personalausweis oder Pass im Baugewerbe ergibt sich aus § 2a SchwarzArbG und wird davon nicht berührt.',
    },
    {
        id: 'bo.regeln.psa_grundausstattung',
        kapitel: 'allgemeine_regelungen',
        titel: 'Grundausstattung an persönlicher Schutzausrüstung',
        wortlaut: 'Im gesamten Baubereich werden Schutzhelm, Sicherheitsschuhe und Warnkleidung getragen. Darüber hinausgehende Schutzausrüstung ergibt sich aus der Gefährdungsbeurteilung des jeweiligen Unternehmens; sie wird von diesem bereitgestellt und ihre Benutzung von ihm durchgesetzt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 2 PSA-Benutzungsverordnung',
            '§ 29 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Die Grundausstattung ist ein Mindestmaß und keine abschließende Festlegung; sie darf die Gefährdungsbeurteilung des Unternehmers nicht ersetzen.',
    },
    {
        id: 'bo.regeln.psa_geltung',
        kapitel: 'allgemeine_regelungen',
        titel: 'Umfang der Tragepflicht und Folgen',
        wortlaut: 'Die Tragepflicht gilt ab {psa_grenze} während der gesamten Anwesenheit im Baubereich; ausgenommen sind Baubüro, Sozial- und Pausenräume sowie die im Baustelleneinrichtungsplan gekennzeichneten Wegeflächen. Wer die vorgeschriebene Schutzausrüstung nicht trägt, wird auf die Regelung hingewiesen und arbeitet erst weiter, nachdem er sie angelegt hat. Bei fortgesetztem Verstoß weist der Auftraggeber die Person vom Baugelände; der Aufsichtführende und der Arbeitgeber der Person werden unterrichtet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 2 PSA-Benutzungsverordnung',
        ],
        hinweis: 'Schutzausrüstung kann selbst Gefährdungen erzeugen; eine pauschale Tragepflicht ohne Ausnahmeprüfung für einzelne Tätigkeiten ist fachlich falsch. Als Folge kommt ausschließlich die Wegweisung in Betracht, niemals eine Geldforderung gegen die Person.',
    },
    {
        id: 'bo.regeln.ordnung_sauberkeit',
        kapitel: 'allgemeine_regelungen',
        titel: 'Ordnung und Sauberkeit',
        wortlaut: 'Jedes Unternehmen räumt seinen Arbeitsbereich laufend und spätestens zum Arbeitsende auf, hält Verkehrswege frei und entfernt seinen Abfall in die dafür bereitgestellten Behälter. Bleibt die Räumung aus, fordert der Auftraggeber sie unter Fristsetzung an; das weitere Vorgehen richtet sich nach dem Bauvertrag.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
            'ArbStättV, Anhang Nr. 5.2 (Baustellen)',
        ],
    },
    {
        id: 'bo.regeln.sozialeinrichtungen',
        kapitel: 'allgemeine_regelungen',
        titel: 'Sanitär-, Wasch- und Pausenräume',
        wortlaut: 'Sanitär- und Waschräume stehen an {sanitaer_standort}, Pausenräume an {pausenraum_standort} zur gemeinsamen Nutzung bereit. Die Einrichtungen werden sauber hinterlassen; Reinigung und Wartung übernimmt {reinigung_verantwortlich}. Reicht die vorhandene Kapazität für die Belegschaft eines Unternehmens nicht aus, stellt dieses für seine Beschäftigten zusätzliche Einrichtungen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A4.1 (Sanitärräume)',
            'ASR A4.2 (Pausen- und Bereitschaftsräume)',
        ],
    },
    {
        id: 'bo.regeln.rauchen_alkohol_drogen',
        kapitel: 'allgemeine_regelungen',
        titel: 'Rauchen, Alkohol und berauschende Mittel',
        wortlaut: 'Rauchen ist im gesamten Baubereich, in allen Bauwerken, Containern und Fahrzeugen untersagt und ausschließlich an der gekennzeichneten Fläche {raucherbereich} erlaubt; offenes Feuer ist außerhalb freigegebener Arbeiten verboten. Der Zutritt zur Baustelle setzt voraus, dass niemand unter der Wirkung von Alkohol, Drogen oder Medikamenten steht, die die Arbeitssicherheit beeinträchtigen; das Mitbringen und der Konsum solcher Mittel auf dem Gelände sind untersagt. Wer erkennbar nicht sicher arbeiten kann, wird vom Baugelände gewiesen; der Aufsichtführende und der Arbeitgeber der Person werden unverzüglich unterrichtet und sorgen für den sicheren Heimweg.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 7 Abs. 2 und § 15 Abs. 2 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            'ASR A2.2 (Maßnahmen gegen Brände)',
            '§ 5 ArbStättV (Nichtraucherschutz)',
        ],
        hinweis: 'Anlasslose Tests, Torkontrollen und Zwangstests sind unzulässig; ein Test kommt nur im begründeten Verdachtsfall und nur mit freiwilliger, widerruflicher Einwilligung in Betracht. Ergebnisse sind Gesundheitsdaten und dürfen beim Auftraggeber nicht gespeichert werden. Das Beschäftigungsverbot bei erkennbarer Arbeitsunfähigkeit trifft den jeweiligen Arbeitgeber.',
    },
    {
        id: 'bo.regeln.bestandsanlagen',
        kapitel: 'allgemeine_regelungen',
        titel: 'Gefährdungen aus Bestand, Medien und Umgebung',
        wortlaut: 'Auf dem Gelände bestehen folgende Gefährdungen aus vorhandenen baulichen Anlagen, Leitungen und der Umgebung: {bestandsgefaehrdungen}. Arbeiten in diesen Bereichen werden vor Beginn mit der Bauleitung abgestimmt; die vorhandenen Bestandsunterlagen und Leitungspläne werden bei {planstelle} eingesehen. Beschränkende Einflüsse für den Bauablauf sind {beschraenkende_einfluesse}.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 5 ArbSchG',
            'RAB 31, Ziff. 3.2',
        ],
        hinweis: 'Der Platzhalter darf nur mit erhobenen, belegten Feststellungen gefüllt werden; eine leere oder allgemein gehaltene Angabe erzeugt den falschen Eindruck, es bestehe nichts.',
    },
    {
        id: 'bo.regeln.foto_film',
        kapitel: 'allgemeine_regelungen',
        titel: 'Foto- und Filmaufnahmen',
        wortlaut: 'Foto-, Film- und Tonaufnahmen auf der Baustelle sind nur mit vorheriger schriftlicher Zustimmung des Auftraggebers zulässig. Davon ausgenommen sind Aufnahmen, die der Sicherheits- und Gesundheitsschutzkoordinator, die Fachkraft für Arbeitssicherheit, der Betriebsarzt, der Betriebsrat, die Aufsichtsbehörde oder der Unfallversicherungsträger zur Erfüllung ihrer Aufgaben anfertigen; ebenso Aufnahmen, die ein Unternehmen zur Dokumentation seiner Gefährdungsbeurteilung oder eines Unfallgeschehens benötigt. Personenaufnahmen werden nicht veröffentlicht.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [
            '§ 3 Abs. 3 BaustellV',
            '§ 5 ArbSchG',
            '§ 6 ASiG',
            '§ 22 KUG',
        ],
        hinweis: 'Ein ausnahmsloses Aufnahmeverbot behindert Überwachung, Gefährdungsbeurteilung und betriebsärztliche Betreuung und ist deshalb unwirksam; die Ausnahmeliste darf nicht gekürzt werden.',
    },
    {
        id: 'bo.regeln.hausrecht_wegweisung',
        kapitel: 'allgemeine_regelungen',
        titel: 'Hausrecht und Wegweisung vom Baugelände',
        wortlaut: 'Der Auftraggeber übt auf dem Baugelände das Hausrecht aus. Er behält sich vor, Personen, die gegen diese Baustellenordnung verstoßen oder andere gefährden, vom Baugelände zu weisen und ihnen den weiteren Zutritt zu verweigern; die Wegweisung ist eine tatsächliche Maßnahme zur Abwehr einer Gefahr und keine Strafe. Der Grund wird schriftlich festgehalten und dem Arbeitgeber der betroffenen Person mitgeteilt; das beauftragte Unternehmen führt seine Leistung mit anderem Personal fort.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§§ 858, 903, 1004 BGB (Besitz und Hausrecht)',
            '§ 123 StGB (Hausfriedensbruch)',
        ],
        hinweis: 'Das Hausrecht steht dem unmittelbaren Besitzer der Baustelle zu, nicht dem Koordinator und nicht dem Verfasser des Dokuments. Eine Wegweisung ohne sachlichen, dokumentierten Grund kann eine Behinderung und eine Vertragsverletzung sein; die Befugnis gehört zusätzlich in den Nachunternehmervertrag.',
    },
    {
        id: 'bo.regeln.folgen_bauvertrag',
        kapitel: 'allgemeine_regelungen',
        titel: 'Weitere Folgen richten sich nach dem Bauvertrag',
        wortlaut: 'Diese Baustellenordnung begründet keine eigenen Zahlungsansprüche, Vertragsstrafen oder Punktesysteme. Folgen von Verstößen ergeben sich ausschließlich aus dem jeweiligen Bauvertrag und den gesetzlichen Bestimmungen. Unberührt bleiben das Recht des Auftraggebers, Arbeiten bei Gefahr im Verzug sofort einzustellen, und die Befugnisse der staatlichen Arbeitsschutzbehörden und der Unfallversicherungsträger.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 Abs. 1 Nr. 1 VOB/B',
            '§ 25 ArbSchG (Bußgeldvorschriften)',
        ],
        hinweis: 'Ein privates Bußgeld gibt es nicht, und eine Vertragsstrafe gegen einen fremden Beschäftigten ist mangels Schuldverhältnis unwirksam. Ein Sanktionskatalog gehört nicht in die Baustellenordnung.',
    },

    // ═══ Kapitel 4 — Verkehrswege ══════════════════════════════════════════
    {
        id: 'bo.wege.zufahrt',
        kapitel: 'verkehrswege',
        titel: 'Zufahrt und Abfahrt',
        wortlaut: 'Die Baustelle wird ausschließlich über die Zufahrt {zufahrt} befahren und über {abfahrt} verlassen. Andere Zu- und Abfahrten, auch über Nachbargrundstücke oder Bauflächen, werden nicht benutzt. Die Tore werden nach jeder Durchfahrt geschlossen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
        ],
    },
    {
        id: 'bo.wege.anlieferung',
        kapitel: 'verkehrswege',
        titel: 'An- und Abtransport',
        wortlaut: 'Anlieferungen erfolgen zwischen {anlieferzeit_beginn} und {anlieferzeit_ende} und werden mindestens {vorlauf_anlieferung} vorher bei {logistikstelle} angemeldet. Fahrzeuge werden nur auf den zugewiesenen Flächen entladen und verlassen die Baustelle unmittelbar danach; ein Abstellen auf Verkehrs-, Rettungs- oder Montageflächen findet nicht statt. Fahrzeuge verlassen das Gelände in einem Zustand, der die öffentlichen Straßen nicht verschmutzt.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [
            '§ 32 StVO (Verkehrshindernisse)',
        ],
    },
    {
        id: 'bo.wege.tempo',
        kapitel: 'verkehrswege',
        titel: 'Geschwindigkeit auf dem Gelände',
        wortlaut: 'Auf dem gesamten Baustellengelände gilt Schrittgeschwindigkeit, höchstens {tempolimit}. Fahrzeugführer passen die Geschwindigkeit den Sicht-, Witterungs- und Untergrundverhältnissen an; Fußgänger haben Vorrang.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
        ],
        hinweis: 'Der zulässige Wert wird projektbezogen festgelegt und nicht aus einer Vorlage übernommen.',
    },
    {
        id: 'bo.wege.trennung',
        kapitel: 'verkehrswege',
        titel: 'Trennung von Fahrzeug- und Personenverkehr',
        wortlaut: 'Fahrzeug- und Fußgängerverkehr werden auf den im Baustelleneinrichtungsplan dargestellten Wegen getrennt geführt. Wo eine Trennung nicht möglich ist, gilt der Bereich {mischverkehrsbereich} als Mischverkehrsfläche mit Vorrang der Fußgänger; sie ist gekennzeichnet und beleuchtet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
            'ArbStättV, Anhang Nr. 5.2 (Baustellen)',
        ],
        hinweis: 'Die Feststellung, dass eine Trennung nicht möglich ist, ist eine fachliche Einzelfallentscheidung und wird begründet dokumentiert.',
    },
    {
        id: 'bo.wege.personenwege',
        kapitel: 'verkehrswege',
        titel: 'Personenwege und Zugänge zum Bauwerk',
        wortlaut: 'Das Bauwerk wird ausschließlich über die eingerichteten Zugänge {bauwerkszugaenge} betreten. Gerüstlagen, Bauaufzüge, Erdmassen, Schalungen und Bewehrung sind keine Verkehrswege. Die Personenwege werden freigehalten, tragfähig und ohne Stolperstellen hergerichtet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
            'ASR A1.5 (Fußböden)',
        ],
    },
    {
        id: 'bo.wege.wege_freihalten',
        kapitel: 'verkehrswege',
        titel: 'Verkehrswege von Material freihalten',
        wortlaut: 'Material, Geräte, Schuttbehälter, Kabel und Schläuche werden nicht auf Verkehrs-, Flucht- und Rettungswegen abgestellt oder verlegt. Ist eine Querung unvermeidbar, wird sie mit einer befahr- und begehbaren Überführung gesichert und gekennzeichnet und nach Arbeitsende zurückgebaut.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
            'ASR A2.3 (Fluchtwege und Notausgänge, Flucht- und Rettungsplan)',
        ],
    },
    {
        id: 'bo.wege.fluchtwege',
        kapitel: 'verkehrswege',
        titel: 'Flucht- und Rettungswege im Bauwerk',
        wortlaut: 'Die Flucht- und Rettungswege im Bauwerk verlaufen über {fluchtwege} und sind gekennzeichnet. Sie bleiben durchgehend begehbar und werden nicht verstellt, verschlossen oder durch Bauzustände unterbrochen. Wird ein Fluchtweg durch den Bauablauf vorübergehend unbenutzbar, meldet das verursachende Unternehmen dies vorher der Bauleitung, die einen gleichwertigen Ersatzweg festlegt und kennzeichnet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A2.3 (Fluchtwege und Notausgänge, Flucht- und Rettungsplan)',
        ],
    },
    {
        id: 'bo.wege.beleuchtung',
        kapitel: 'verkehrswege',
        titel: 'Allgemeinbeleuchtung der Wege',
        wortlaut: 'Die Allgemeinbeleuchtung der Verkehrswege, Zugänge und gemeinsam genutzten Flächen stellt {beleuchtung_verantwortlich} bereit und hält sie in Betrieb. Ausfälle werden unverzüglich der Bauleitung gemeldet. Die Beleuchtung der eigenen Arbeitsplätze richtet jedes Unternehmen selbst ein.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A3.4 (Beleuchtung und Sichtverbindung)',
        ],
    },
    {
        id: 'bo.wege.winterdienst',
        kapitel: 'verkehrswege',
        titel: 'Räumen und Streuen',
        wortlaut: 'Räum- und Streudienst auf den gemeinsam genutzten Verkehrswegen, Zugängen und Sozialflächen übernimmt {winterdienst_verantwortlich} ab {winterdienst_beginn}. Schnee und Eis in den eigenen Arbeitsbereichen und auf den zugehörigen Zugängen beseitigt jedes Unternehmen selbst, bevor es dort Arbeiten aufnimmt.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [
            'ASR A1.8 (Verkehrswege)',
        ],
        wenn: ['winterbau'],
        bedingungText: 'nur bei Bauzeit im Winterhalbjahr',
    },
    {
        id: 'bo.wege.rueckwaerts',
        kapitel: 'verkehrswege',
        titel: 'Rückwärtsfahren und Einweisen',
        wortlaut: 'Rückwärtsfahren wird auf das notwendige Maß beschränkt und findet nur statt, wenn der Fahrweg vollständig eingesehen wird oder eine eingewiesene Person einweist. Der Einweiser hält sich außerhalb des Fahrwegs und im Sichtfeld des Fahrers auf und trägt Warnkleidung.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 7 DGUV Vorschrift 38 (Bauarbeiten)',
            'ASR A1.8 (Verkehrswege)',
        ],
    },
    {
        id: 'bo.wege.oeffentlicher_raum',
        kapitel: 'verkehrswege',
        titel: 'Arbeiten im öffentlichen Verkehrsraum',
        wortlaut: 'Arbeiten, die den öffentlichen Verkehrsraum berühren, werden erst nach vorliegender verkehrsrechtlicher Anordnung aufgenommen; die Anordnung führt {verkehrsrecht_verantwortlich} herbei. Die Absperrung wird nach dem angeordneten Verkehrszeichenplan aufgestellt, arbeitstäglich kontrolliert und bei Abweichungen sofort in Ordnung gebracht.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 45 Abs. 6 StVO',
            'RSA 21 (Richtlinien für die verkehrsrechtliche Sicherung von Arbeitsstellen an Straßen)',
            'ASR A5.2 (Straßenbaustellen)',
        ],
        wenn: ['strassenbaustelle'],
        bedingungText: 'nur wenn öffentlicher Verkehrsraum berührt wird',
        hinweis: 'Eigenmächtiges Aufstellen oder Verändern der Absperrung ohne Anordnung begründet die Haftung für Folgeschäden.',
    },
    {
        id: 'bo.wege.verkehrssicherung_dritte',
        kapitel: 'verkehrswege',
        titel: 'Sicherung gegenüber Dritten',
        wortlaut: 'Die Baustelle wird gegen unbefugtes Betreten gesichert; Umzäunung, Zugangssicherung und Beschilderung stellt {sicherung_verantwortlich}. Jedes Unternehmen sichert die von seinen Arbeiten ausgehenden Gefahrstellen — Öffnungen, Gräben, Überstände, herabfallende Gegenstände — auch gegenüber Passanten, Anliegern und Nachbarn und stellt beschädigte Absperrungen unverzüglich wieder her.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 823 BGB (Verkehrssicherungspflicht)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
    },

    // ═══ Kapitel 5 — Anschlüsse und Verteilungen ═══════════════════════════
    {
        id: 'bo.anschluesse.baustrom_bereitstellung',
        kapitel: 'anschluesse',
        titel: 'Baustromversorgung',
        wortlaut: 'Die Baustromversorgung stellt {baustrom_verantwortlich} über die Verteiler an {baustrom_standorte} bereit. Strom wird ausschließlich an diesen Verteilern entnommen; Anschlüsse an Bestandsinstallationen, Steckdosen des Bauwerks oder fremde Verteiler werden nicht genutzt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DIN VDE 0100-704 (Errichten von Niederspannungsanlagen — Baustellen)',
            'DGUV Information 203-006 (Auswahl und Betrieb elektrischer Anlagen und Betriebsmittel auf Bau- und Montagestellen)',
        ],
    },
    {
        id: 'bo.anschluesse.verteiler_eingriff',
        kapitel: 'anschluesse',
        titel: 'Eingriffe in die Verteilung',
        wortlaut: 'Arbeiten an den Baustromverteilern, an der Zuleitung und an der Bestandsinstallation führt ausschließlich eine Elektrofachkraft im Auftrag von {baustrom_verantwortlich} aus. Abschaltungen werden mindestens {vorlauf_abschaltung} vorher angekündigt; wiedereingeschaltet wird erst nach Freigabe durch die Person, die die Abschaltung veranlasst hat.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Vorschrift 3 (Elektrische Anlagen und Betriebsmittel)',
        ],
        hinweis: 'Unangekündigte Abschaltungen gefährden laufende Arbeiten mit Hebezeugen, Wasserhaltung und Beleuchtung; die Ankündigung ist Teil der Koordinierung, nicht Höflichkeit.',
    },
    {
        id: 'bo.anschluesse.betriebsmittel_pruefung',
        kapitel: 'anschluesse',
        titel: 'Eigene elektrische Betriebsmittel',
        wortlaut: 'Jedes Unternehmen setzt nur elektrische Betriebsmittel und Leitungen ein, die für den Baustellenbetrieb geeignet sind und deren Prüfung durch eine befähigte Person nachgewiesen ist. Der Nachweis wird der Bauleitung auf Verlangen vorgelegt. Erkennbar beschädigte Betriebsmittel werden sofort der Nutzung entzogen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 14 BetrSichV (Prüfung von Arbeitsmitteln)',
            'DGUV Vorschrift 3 (Elektrische Anlagen und Betriebsmittel)',
            'TRBS 1203 (Zur Prüfung befähigte Personen)',
        ],
    },
    {
        id: 'bo.anschluesse.leitungsfuehrung',
        kapitel: 'anschluesse',
        titel: 'Verlegung von Kabeln und Schläuchen',
        wortlaut: 'Anschlussleitungen und Schläuche werden so geführt, dass sie nicht überfahren, gequetscht oder zur Stolperstelle werden; über Verkehrswege werden sie abgehängt oder mit Überfahrschutz gesichert. Nicht mehr benötigte Leitungen entfernt das verlegende Unternehmen unverzüglich.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Information 203-006 (Auswahl und Betrieb elektrischer Anlagen und Betriebsmittel auf Bau- und Montagestellen)',
            'ASR A1.8 (Verkehrswege)',
        ],
    },
    {
        id: 'bo.anschluesse.bauwasser',
        kapitel: 'anschluesse',
        titel: 'Bauwasserentnahme',
        wortlaut: 'Bauwasser wird ausschließlich an den Entnahmestellen {bauwasser_standorte} entnommen. Die Entnahme wird bei {bauwasser_verantwortlich} angemeldet; die Abrechnung erfolgt nach {bauwasser_abrechnung}. Schläuche werden nach Gebrauch entleert und abgenommen.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [],
    },
    {
        id: 'bo.anschluesse.trinkwasserschutz',
        kapitel: 'anschluesse',
        titel: 'Trinkwasser und Rückfließschutz',
        wortlaut: 'Trinkwasserentnahmestellen sind als solche gekennzeichnet; Entnahmestellen für Brauchwasser tragen den Hinweis „kein Trinkwasser". An jede Entnahmestelle, an der Bauwasser entnommen wird, wird eine geeignete Sicherungseinrichtung gegen Rückfließen gesetzt. Schlauchenden werden nicht in Behälter, Gräben oder stehendes Wasser gelegt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'TrinkwV (Trinkwasserverordnung)',
            'DIN EN 1717 (Schutz des Trinkwassers vor Verunreinigungen in Trinkwasser-Installationen)',
        ],
        hinweis: 'Ein Rückfließen aus der Baustelle in die Trinkwasserinstallation ist ein meldepflichtiger Schaden mit Folgen weit über die Baustelle hinaus.',
    },
    {
        id: 'bo.anschluesse.schmutzwasser',
        kapitel: 'anschluesse',
        titel: 'Schmutzwasser',
        wortlaut: 'Schmutzwasser aus Sozialeinrichtungen und Reinigungsvorgängen wird ausschließlich über {schmutzwasser_anschluss} abgeleitet. Waschwasser aus Betonarbeiten, Farbreste, Öle und Emulsionen gelangen nicht in die Schmutz- oder Regenwasserkanalisation, sondern werden gesammelt und über {entsorgung_verantwortlich} entsorgt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 55 WHG (Grundsätze der Abwasserbeseitigung)',
        ],
    },
    {
        id: 'bo.anschluesse.niederschlagswasser',
        kapitel: 'anschluesse',
        titel: 'Niederschlagswasser und Wasserhaltung',
        wortlaut: 'Niederschlags- und Sickerwasser wird über {niederschlagswasser_weg} abgeführt; die Wasserhaltung betreibt {wasserhaltung_verantwortlich}. Jedes Unternehmen hält seine Arbeitsbereiche so vor, dass Wasser abfließen kann, und meldet Vernässungen, die andere Gewerke betreffen, unverzüglich der Bauleitung.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [
            '§ 55 WHG (Grundsätze der Abwasserbeseitigung)',
        ],
        wenn: ['erdarbeiten'],
        bedingungText: 'nur bei Erdbau, Baugruben oder Gründungsarbeiten',
    },
    {
        id: 'bo.anschluesse.abfall_getrennt',
        kapitel: 'anschluesse',
        titel: 'Abfalltrennung und Behälter',
        wortlaut: 'Bau- und Abbruchabfälle werden getrennt gesammelt; Behälter für {abfallfraktionen} stehen an {abfall_standorte}. Jedes Unternehmen entsorgt seine eigenen Abfälle in die richtige Fraktion und stellt keine Abfälle außerhalb der Behälter ab. Falsch befüllte Behälter sortiert das verursachende Unternehmen auf Aufforderung der Bauleitung nach.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 8 GewAbfV (Getrennte Sammlung, Vorbereitung zur Wiederverwendung und Recycling von bestimmten Bau- und Abbruchabfällen)',
            '§ 9 KrWG',
        ],
    },
    {
        id: 'bo.anschluesse.gefahrstoffe_lagerung',
        kapitel: 'anschluesse',
        titel: 'Gefahrstoffe und wassergefährdende Stoffe',
        wortlaut: 'Kraftstoffe, Öle, Lösemittel und andere wassergefährdende Stoffe werden ausschließlich auf {gefahrstoff_lagerflaeche} in dichten, aufgefangenen Behältern gelagert; Betankungen finden nur dort oder auf befestigten, aufgefangenen Flächen statt. Jedes Unternehmen hält für seine Stoffe Bindemittel bereit und meldet jeden Austritt sofort der Bauleitung. Die Sicherheitsdatenblätter der eingesetzten Stoffe liegen an der Arbeitsstelle vor.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 6 GefStoffV (Informationsermittlung und Gefährdungsbeurteilung)',
            'AwSV (Verordnung über Anlagen zum Umgang mit wassergefährdenden Stoffen)',
        ],
        hinweis: 'Welche Stoffe zusammen gelagert werden dürfen, ergibt sich aus der Gefährdungsbeurteilung des einlagernden Unternehmens; die Baustellenordnung weist nur die Fläche zu.',
    },
    {
        id: 'bo.anschluesse.mitbenutzung',
        kapitel: 'anschluesse',
        titel: 'Mitbenutzung fremder Anschlüsse',
        wortlaut: 'Anschlüsse, Verteiler, Behälter und Einrichtungen anderer Unternehmen werden nicht ohne deren ausdrückliche Zustimmung mitbenutzt. Wer eine Einrichtung eines anderen Unternehmens beschädigt, meldet dies unverzüglich der Bauleitung und dem betroffenen Unternehmen.',
        art: 'regel',
        stufe: 'ueblich',
        vorschriften: [],
    },

    // ═══ Kapitel 6 — Arbeitsmittel mit unternehmensübergreifender Gefährdung ═══
    {
        id: 'bo.arbeitsmittel.ueberlassung',
        kapitel: 'arbeitsmittel',
        titel: 'Überlassung von Arbeitsmitteln zwischen Unternehmen',
        wortlaut: 'Arbeitsmittel werden anderen Unternehmen nur nach schriftlicher Vereinbarung überlassen, in der Umfang, Zeitraum, Zustand, Prüfnachweis und die Verantwortung für Wartung und Prüfung während der Überlassung festgehalten sind. Ohne diese Vereinbarung benutzt kein Unternehmen die Arbeitsmittel eines anderen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 BetrSichV (Grundpflichten des Arbeitgebers)',
            '§ 6 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Die Benutzerpflichten nach BetrSichV treffen den Arbeitgeber der benutzenden Beschäftigten; eine Überlassung ohne klare Zuordnung lässt Prüf- und Unterweisungspflichten offen.',
    },
    {
        id: 'bo.arbeitsmittel.gefahrbereich',
        kapitel: 'arbeitsmittel',
        titel: 'Gefahrbereiche von Maschinen',
        wortlaut: 'Der Gefahrbereich arbeitender Maschinen wird nicht betreten. Wer sich einer arbeitenden Maschine nähern muss, nimmt vorher Blickkontakt mit dem Maschinenführer auf und tritt erst ein, nachdem dieser die Bewegung eingestellt und den Zutritt bestätigt hat.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 BetrSichV',
            'DGUV Vorschrift 38 (Bauarbeiten)',
        ],
    },
    {
        id: 'bo.arbeitsmittel.erdbau_leitungen',
        kapitel: 'arbeitsmittel',
        titel: 'Erdarbeiten und vorhandene Leitungen',
        wortlaut: 'Vor Beginn von Erd- und Rammarbeiten holt das ausführende Unternehmen Leitungsauskünfte ein, ortet den Leitungsbestand und legt bei Bedarf Suchschlitze an; die vorliegenden Auskünfte werden bei {planstelle} eingesehen. Arbeiten unter elektrischen Freileitungen werden vor Beginn mit der Bauleitung und dem Netzbetreiber abgestimmt; bei Annäherungen von weniger als 5 m an Hochspannungsleitungen werden die Maßnahmen mit dem Netzbetreiber schriftlich festgelegt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Vorschrift 38 (Bauarbeiten)',
            'DGUV Vorschrift 3 (Elektrische Anlagen und Betriebsmittel)',
            'Anhang II Nr. 4 BaustellV',
        ],
        wenn: ['erdarbeiten', 'abbruch'],
        bedingungText: 'nur bei Erd-, Ramm-, Bohr- oder Abbrucharbeiten; der zweite Satz nur bei Freileitungen im oder am Baufeld',
        hinweis: 'Die Angabe „weniger als 5 m" stammt aus Anhang II BaustellV und beschreibt das Merkmal, nicht den einzuhaltenden Schutzabstand; dieser wird für den Einzelfall vom Netzbetreiber festgelegt.',
    },
    {
        id: 'bo.arbeitsmittel.geruest_uebergabe',
        kapitel: 'arbeitsmittel',
        titel: 'Übergabe und Freigabe von Gerüsten',
        wortlaut: 'Ein Gerüst wird erst benutzt, nachdem der Ersteller es geprüft und schriftlich übergeben hat und die Freigabekennzeichnung am Zugang angebracht ist. Die Kennzeichnung nennt den Ersteller, die Lastklasse, die zulässige Nutzung sowie Datum und Ergebnis der Prüfung. Nicht freigegebene oder gesperrte Gerüstbereiche werden nicht betreten.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 14 BetrSichV (Prüfung von Arbeitsmitteln)',
            'TRBS 2121 Teil 1 (Gefährdung von Beschäftigten durch Absturz bei der Verwendung von Gerüsten)',
        ],
        wenn: ['geruest'],
        bedingungText: 'nur wenn Gerüste vorhanden sind',
    },
    {
        id: 'bo.arbeitsmittel.geruest_pruefung_nutzer',
        kapitel: 'arbeitsmittel',
        titel: 'Sichtprüfung durch das nutzende Unternehmen',
        wortlaut: 'Jedes nutzende Unternehmen prüft das Gerüst vor jeder Benutzung durch Inaugenscheinnahme auf offensichtliche Mängel — fehlender Seitenschutz, fehlende Beläge, fehlende Verankerung, beschädigte Bauteile, fehlende Kennzeichnung. Bei Mängeln wird die Nutzung eingestellt, der Bereich gesperrt und die Bauleitung sowie der Gerüstersteller unverzüglich unterrichtet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'TRBS 2121 Teil 1 (Gefährdung von Beschäftigten durch Absturz bei der Verwendung von Gerüsten)',
        ],
        wenn: ['geruest'],
        bedingungText: 'nur wenn Gerüste vorhanden sind',
    },
    {
        id: 'bo.arbeitsmittel.geruest_aenderungsverbot',
        kapitel: 'arbeitsmittel',
        titel: 'Änderungsverbot am Gerüst',
        wortlaut: 'Bauteile des Gerüsts — Beläge, Seitenschutz, Verankerungen, Leitergänge, Konsolen — werden von nutzenden Unternehmen nicht entfernt, versetzt oder verändert. Erforderliche Änderungen führt ausschließlich der Gerüstersteller aus; sie werden über die Bauleitung beauftragt, und die Nutzung wird bis zur erneuten Freigabe eingestellt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'TRBS 2121 Teil 1 (Gefährdung von Beschäftigten durch Absturz bei der Verwendung von Gerüsten)',
            'DGUV Vorschrift 38 (Bauarbeiten)',
        ],
        wenn: ['geruest'],
        bedingungText: 'nur wenn Gerüste vorhanden sind',
        hinweis: 'Das eigenmächtige Entfernen von Seitenschutz ist eine der häufigsten Ursachen für Abstürze aus Gerüsten und trifft regelmäßig Beschäftigte anderer Unternehmen als das entfernende.',
    },
    {
        id: 'bo.arbeitsmittel.bauaufzug',
        kapitel: 'arbeitsmittel',
        titel: 'Benutzung des Bauaufzugs',
        wortlaut: 'Der Bauaufzug an {bauaufzug_standort} wird von {bauaufzug_betreiber} betrieben. Er wird nur von unterwiesenen Personen bedient, nur bis zur angeschriebenen Tragfähigkeit belastet und nur an den freigegebenen Haltestellen be- und entladen. Die Mitfahrt von Personen ist nur zulässig, wenn der Aufzug dafür zugelassen ist und dies angeschrieben ist.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 BetrSichV',
            'DGUV Vorschrift 38 (Bauarbeiten)',
        ],
        bedingungText: 'nur wenn ein Bauaufzug vorhanden ist',
    },
    {
        id: 'bo.arbeitsmittel.hubarbeitsbuehne',
        kapitel: 'arbeitsmittel',
        titel: 'Hubarbeitsbühnen',
        wortlaut: 'Hubarbeitsbühnen werden nur von Personen bedient, die dafür ausgebildet, geeignet und vom Unternehmer schriftlich beauftragt sind. Der Aufstellbereich wird gegen Anfahren und gegen Betreten unter dem Arbeitskorb gesichert und mit der Bauleitung abgestimmt, wenn er Verkehrswege oder Arbeitsbereiche anderer Unternehmen berührt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 BetrSichV',
            'DGUV Information 208-019 (Sicherer Umgang mit fahrbaren Hubarbeitsbühnen)',
        ],
        bedingungText: 'nur wenn Hubarbeitsbühnen eingesetzt werden',
    },
    {
        id: 'bo.arbeitsmittel.kran_betrieb',
        kapitel: 'arbeitsmittel',
        titel: 'Kranbetrieb und Anschlagen von Lasten',
        wortlaut: 'Krane werden nur von beauftragten Kranführern bedient; Lasten werden nur von beauftragten Anschlägern angeschlagen. Kranbetreiber ist {kran_betreiber}. Vor der Nutzung eines Krans durch ein anderes Unternehmen wird die Überlassung schriftlich vereinbart, einschließlich der Zuständigkeit für Kranführer, Anschlagmittel und Prüfungen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Vorschrift 52 (Krane)',
            'DGUV Regel 100-500 (Betreiben von Arbeitsmitteln)',
        ],
        wenn: ['kran'],
        bedingungText: 'nur wenn Kran vorhanden',
    },
    {
        id: 'bo.arbeitsmittel.kran_vorfahrt',
        kapitel: 'arbeitsmittel',
        titel: 'Vorfahrt bei sich überschneidenden Schwenkbereichen',
        wortlaut: 'Die Schwenkbereiche der Krane {kranliste} überschneiden sich in den im Baustelleneinrichtungsplan dargestellten Feldern. In diesen Feldern gilt: {kran_vorfahrtregel}; der wartepflichtige Kranführer hält seinen Ausleger außerhalb des gemeinsamen Feldes, bis der vorfahrtberechtigte Kran es verlassen hat. Die Kranführer stehen während des Betriebs über {kran_funkkanal} in ständiger Verbindung; bei Ausfall der Verbindung wird der Betrieb in den gemeinsamen Feldern eingestellt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Vorschrift 52 (Krane)',
            '§ 6 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            'RAB 31, Ziff. 3.2',
        ],
        bedingungText: 'nur wenn mehrere Krane mit sich überschneidenden Schwenkbereichen betrieben werden',
        hinweis: 'Die Vorfahrtregel wird für das konkrete Kranfeld festgelegt und darf keiner Vorlage entnommen werden; sie tritt neben die Höhenstaffelung und wird mit allen Kranbetreibern schriftlich vereinbart.',
    },
    {
        id: 'bo.arbeitsmittel.kran_lastueberflug',
        kapitel: 'arbeitsmittel',
        titel: 'Lastenbewegung über Arbeitsbereiche',
        wortlaut: 'Lasten werden nicht über Arbeitsplätzen, Verkehrswegen, Sozialeinrichtungen und Bereichen außerhalb der Baustelle bewegt. Lässt sich ein Überfahren nicht vermeiden, wird der betroffene Bereich vorher geräumt und abgesperrt; die Räumung stimmt der Kranbetreiber mit der Bauleitung und den betroffenen Unternehmen ab.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'DGUV Vorschrift 52 (Krane)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
        wenn: ['kran'],
        bedingungText: 'nur wenn Kran oder anderes Hebezeug vorhanden',
    },
    {
        id: 'bo.arbeitsmittel.laerm',
        kapitel: 'arbeitsmittel',
        titel: 'Lärmintensive Arbeiten',
        wortlaut: 'Lärmintensive Arbeiten werden der Bauleitung vor Beginn angezeigt, damit betroffene Nachbargewerke zeitlich oder räumlich getrennt werden. Wo die unteren Auslösewerte von 80 dB(A) beziehungsweise 135 dB(C) erreicht oder überschritten werden, stellt jeder Arbeitgeber seinen Beschäftigten Gehörschutz bereit; ab den oberen Auslösewerten von 85 dB(A) beziehungsweise 137 dB(C) wird Gehörschutz getragen und der Bereich gekennzeichnet.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 6 LärmVibrationsArbSchV (Auslösewerte bei Lärm)',
            '§ 8 LärmVibrationsArbSchV (Gehörschutz)',
        ],
        bedingungText: 'nur bei lärmintensiven Arbeiten, die Beschäftigte anderer Unternehmen erreichen',
        hinweis: 'Auslösewerte sind keine Expositionsgrenzwerte; die Ermittlung der Exposition und die Festlegung der Maßnahmen bleiben Sache des jeweiligen Arbeitgebers.',
    },

    // ═══ Kapitel 7 — Gemeinsam genutzte Schutzeinrichtungen gegen Absturz ═══
    {
        id: 'bo.absturz.rangfolge',
        kapitel: 'absturzsicherung',
        titel: 'Rangfolge der Absturzsicherungen',
        wortlaut: 'Gegen Absturz wird zuerst mit baulichen und technischen Einrichtungen gesichert, die ohne Zutun der einzelnen Person wirken; erst wenn das nach den örtlichen Gegebenheiten nicht ausführbar ist, kommen Auffangeinrichtungen und danach persönliche Schutzausrüstung gegen Absturz in Betracht. Die Feststellung, dass eine vorrangige Maßnahme nicht ausführbar ist, trifft das ausführende Unternehmen fachkundig und hält sie in seiner Gefährdungsbeurteilung schriftlich fest.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 ArbSchG',
            'ASR A2.1 Nr. 4.2 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
        hinweis: 'Der Griff zur persönlichen Schutzausrüstung ohne dokumentierte Prüfung der vorrangigen Maßnahmen ist der am häufigsten beanstandete Fehler in dieser Rangfolge.',
    },
    {
        id: 'bo.absturz.schwellen',
        kapitel: 'absturzsicherung',
        titel: 'Ab welcher Höhe gesichert wird',
        wortlaut: 'An Treppen, Wandöffnungen und Verkehrswegen wird ab 1,00 m Absturzhöhe gegen Absturz gesichert, an allen übrigen Arbeitsplätzen ab 2,00 m. Auf geneigten Dächern und an Geschossdecken ist eine Absturzsicherung nur unter den in der Vorschrift genannten Bedingungen bis 3,00 m entbehrlich; ob diese Bedingungen vorliegen, beurteilt das ausführende Unternehmen im Einzelfall. Unabhängig von der Höhe wird gesichert, wo besondere Gefahren bestehen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
        hinweis: 'Eine pauschale Angabe „ab 2 m" ist falsch und lässt die 1,00-m-Fälle ungesichert; die 3,00-m-Regelung ist ein an Bedingungen geknüpfter Sonderfall und keine allgemeine Schwelle.',
    },
    {
        id: 'bo.absturz.seitenschutz',
        kapitel: 'absturzsicherung',
        titel: 'Seitenschutz an Rändern und Öffnungen',
        wortlaut: 'Absturzkanten, Deckenränder, Treppenläufe, Podeste und Wandöffnungen werden mit vollständigem Seitenschutz aus Geländerholm, Zwischenholm und Bordbrett gesichert. Den Seitenschutz stellt {absturzsicherung_verantwortlich} für die gemeinsam genutzten Bereiche; für die eigenen Arbeitsbereiche stellt ihn das jeweils dort arbeitende Unternehmen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
    },
    {
        id: 'bo.absturz.abdeckungen',
        kapitel: 'absturzsicherung',
        titel: 'Abdeckungen über Öffnungen',
        wortlaut: 'Öffnungen in Decken, Böden und Verkehrsflächen werden tragfähig abgedeckt, gegen Verschieben gesichert und als Abdeckung gekennzeichnet. Abdeckungen werden nicht als Lager- oder Arbeitsfläche und nicht als Absetzfläche für Lasten genutzt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 10 DGUV Vorschrift 38 (Bauarbeiten)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
    },
    {
        id: 'bo.absturz.auffangeinrichtungen',
        kapitel: 'absturzsicherung',
        titel: 'Fanggerüste und Schutznetze',
        wortlaut: 'Wo Absturzsicherungen nicht ausführbar sind, werden Auffangeinrichtungen eingesetzt: Fanggerüste, Dachfanggerüste oder Schutznetze. Sie werden vom Ersteller montiert, geprüft und schriftlich übergeben; die Nutzung beginnt erst nach dieser Übergabe. Auffangeinrichtungen werden auf die im Übergabeprotokoll genannte Nutzung beschränkt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
        bedingungText: 'nur wenn Auffangeinrichtungen vorgesehen sind',
    },
    {
        id: 'bo.absturz.uebergabe',
        kapitel: 'absturzsicherung',
        titel: 'Übergabe gemeinsam genutzter Schutzeinrichtungen',
        wortlaut: 'Schutzeinrichtungen gegen Absturz, die von mehreren Unternehmen genutzt werden, werden schriftlich übergeben. Das Protokoll benennt den Bereich, die Art der Einrichtung, den Ersteller, den Zeitpunkt und die Person, die für Erhalt und Kontrolle bis zur Rücknahme zuständig ist. Die Bauleitung führt die Protokolle in {ablage_uebergaben}.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 6 DGUV Vorschrift 1 (Grundsätze der Prävention)',
            'RAB 31, Ziff. 3.2',
        ],
    },
    {
        id: 'bo.absturz.entfernen_freigabe',
        kapitel: 'absturzsicherung',
        titel: 'Entfernen nur nach Freigabe',
        wortlaut: 'Schutzeinrichtungen gegen Absturz werden nur entfernt, wenn die Bauleitung dies vorher schriftlich freigegeben hat. Der Antrag nennt den Bereich, den Grund, den Zeitraum und die Ersatzmaßnahme für die Dauer der Entfernung. Ohne Freigabe bleibt jede Einrichtung an ihrem Platz, auch wenn sie die eigene Arbeit behindert.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
            '§ 6 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
        hinweis: 'Entfernte Sicherungen treffen fast immer ein anderes Unternehmen als das entfernende; ohne Freigabeverfahren bleibt die Verursachung im Schadensfall ungeklärt.',
    },
    {
        id: 'bo.absturz.ersatzmassnahme',
        kapitel: 'absturzsicherung',
        titel: 'Sicherung während der Entfernung',
        wortlaut: 'Solange eine Schutzeinrichtung entfernt ist, wird der Bereich gegen Betreten abgesperrt und gekennzeichnet; reicht das nicht aus, wird der Bereich beaufsichtigt. Die Absperrung stellt das Unternehmen, das die Einrichtung entfernt hat, und hält sie bis zur Wiederherstellung aufrecht.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            'ASR A2.1 (Schutz vor Absturz und herabfallenden Gegenständen, Betreten von Gefahrenbereichen)',
        ],
    },
    {
        id: 'bo.absturz.wiederherstellung',
        kapitel: 'absturzsicherung',
        titel: 'Wiederherstellungspflicht',
        wortlaut: 'Wer eine Schutzeinrichtung entfernt, stellt sie unmittelbar nach Abschluss der Arbeiten in vollem Umfang wieder her, spätestens vor Verlassen der Arbeitsstelle und vor Arbeitsende. Die Wiederherstellung wird der Bauleitung gemeldet; erst danach endet die Absperrung.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
        ],
    },
    {
        id: 'bo.absturz.meldepflicht',
        kapitel: 'absturzsicherung',
        titel: 'Melden fehlender oder beschädigter Sicherungen',
        wortlaut: 'Wer eine fehlende, beschädigte oder unwirksame Absturzsicherung feststellt, sichert die Stelle sofort gegen Betreten, soweit ihm das gefahrlos möglich ist, und meldet sie unverzüglich der Bauleitung unter {telefon_bauleitung}. Der Fund wird nicht stillschweigend umgangen und nicht dem nächsten Gewerk überlassen.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 16 Abs. 1 ArbSchG (Besondere Unterstützungspflichten)',
            '§ 16 DGUV Vorschrift 1 (Grundsätze der Prävention)',
        ],
    },
    {
        id: 'bo.absturz.psa_anschlagpunkte',
        kapitel: 'absturzsicherung',
        titel: 'Persönliche Schutzausrüstung gegen Absturz',
        wortlaut: 'Wo persönliche Schutzausrüstung gegen Absturz eingesetzt wird, benennt das ausführende Unternehmen die Anschlagpunkte, weist ihre Tragfähigkeit nach und unterweist die Benutzer; die Rettung aus dem Auffanggurt wird vor Arbeitsbeginn geplant und die Mittel dafür werden vorgehalten. Anschlagpunkte anderer Unternehmen werden nur nach schriftlicher Zustimmung des Errichters benutzt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 9 DGUV Vorschrift 38 (Bauarbeiten)',
            '§ 3 PSA-Benutzungsverordnung',
            'ASR A2.1 Nr. 4.2',
        ],
        bedingungText: 'nur wenn persönliche Schutzausrüstung gegen Absturz eingesetzt wird',
        hinweis: 'Persönliche Schutzausrüstung gegen Absturz ohne geplante und geübte Rettung ist unvollständig; ein Hängetrauma tritt binnen kurzer Zeit ein.',
    },
    {
        id: 'bo.absturz.kontrolle',
        kapitel: 'absturzsicherung',
        titel: 'Regelmäßige Kontrolle der Schutzeinrichtungen',
        wortlaut: 'Die gemeinsam genutzten Schutzeinrichtungen werden von {kontrolle_verantwortlich} in Abständen von {kontrollintervall} und nach jedem Ereignis kontrolliert, das ihre Wirksamkeit beeinträchtigt haben kann — Sturm, Anfahren, Montageeingriffe. Das Ergebnis wird schriftlich festgehalten; festgestellte Mängel werden vor Wiederaufnahme der Arbeiten im betroffenen Bereich beseitigt.',
        art: 'regel',
        stufe: 'pflicht',
        vorschriften: [
            '§ 4 ArbSchG',
            '§ 3 Abs. 3 BaustellV',
        ],
    },
];

/** Alle Bausteine in der Reihenfolge der Vorlage. */
export const KATALOG: readonly Baustein[] = BAUSTEINE.map(b => ({
    ...b,
    platzhalter: platzhalterImWortlaut(b.wortlaut),
}));

const NACH_ID = new Map(KATALOG.map(b => [b.id, b]));

/** Die Bausteine eines Kapitels, in der Reihenfolge der Vorlage. */
export const bausteineDesKapitels = (kapitel: string): Baustein[] =>
    KATALOG.filter(b => b.kapitel === kapitel);

/** Ein Baustein nach seiner Kennung; `undefined`, wenn es sie nicht gibt. */
export const bausteinNachId = (id: string): Baustein | undefined => NACH_ID.get(id);
